import React, { CSSProperties, MutableRefObject, useRef, useState } from 'react'
import { AgTechDialogRenderProps } from '@agtech/core/functions/AgTechAppDialogs';
import { buildDataActionExecutor, useDataActionExecutionContextProps } from '@agtech/core/data/actions/AgTechDataActions';
import { IAgTechAppDataActions } from '@agtech/core/functions/AgTechAppDataActions';
import { useAgTechAuthContext } from '@agtech/core/auth/AgTechAuthContext';
import { userHasPermission } from '@agtech/core/auth/AgTechAuthData';
import { executeWithMinimumDelay } from '@agtech/core/utilities/AgTechUtilities';
import { AgTechWebAppContext, useAgTechWebAppContext } from '../App/Contexts/AgTechWebAppContext';
import { AgTechModalRenderProps } from '../Portals/Modals/AgTechModal';
import { AgTechPopUpRenderProps } from '../App/Functions/AgTechPopUps';
import { AgTechPopUpOnHoverText } from '../Portals/Popups/AgTechPopUpOnHoverContainer';
import AgTechDialogSlot from '../Portals/Dialogs/AgTechDialogSlot';
import { logDev } from '@agtech/core/logging/AgTechLogger';
import { AgTechPopUpCoreProps, AgTechPopUpPosition } from '@agtech/core/functions/AgTechAppPopUps';
import { v4 as uuidv4 } from 'uuid';
import { gridItems } from '../Grids/Data/AgTechGridProps';

export interface IShowDialog {
    openDialog: (dialogRendering: AgTechDialogRenderProps) => void
}

export interface IShowPopUp {
    openPopup: (popupRendering: AgTechPopUpRenderProps) => void
}
export interface IShowModal {
    openModal: (modal: AgTechModalRenderProps) => void
}

export interface IShowToasts {
    showError: (errorMessage: string) => void
}

export type AgTechRendering = {
    rendering: () => React.ReactNode
}

export type AgTechPopUpRendering = AgTechRendering & {
    position?: AgTechPopUpPosition,
    emphasizedShadow?: boolean
}

export type AgTechWebClickable = AgTechWebAppContext & {
    actions: IAgTechAppDataActions,
    clickableRef: React.RefObject<any>,
    renderPopup: (popup: () => React.ReactNode) => void,
    content: () => React.ReactNode
};

type ClickableDialog = AgTechDialogRenderProps & {
    id: string
}

export const useClickable = (ref: React.RefObject<any>): AgTechWebClickable => {
    let webAppContext = useAgTechWebAppContext();
    let dataActionExecutionProps = useDataActionExecutionContextProps();
    const [dialog, setDialog] = useState<ClickableDialog | undefined>(undefined);

    let buttonClickable: AgTechWebClickable = {
        ...webAppContext,
        dialogs: {
            dialogs: [],
            openDialog: (dialog: AgTechDialogRenderProps) => {
                setDialog({
                    ...dialog,
                    id: uuidv4()
                });
            },
            closeDialog: () => {
                logDev(['[Closing dialog]', dialog]);
                setDialog(undefined);
            }
        },
        actions: {
            executeDataAction: async (action, executionProps) => {
                return await buildDataActionExecutor(dataActionExecutionProps).executeAction(action, {
                    submittedEntity: executionProps.submittedEntity,
                    originalEntity: executionProps.originalEntity
                });
            },
            executeDataActionInModal: async (action, props) => {
                logDev('Executing action in modal', action, props);

                webAppContext.modals.showActionModal({
                    action: action,
                    actionProps: props
                });
            },
            executeDataActionWithVisual: async (action, executionProps, visualProps) => {
                let actionExecutor = buildDataActionExecutor(dataActionExecutionProps);
                let loadingModalId = '';

                let result = await executeWithMinimumDelay({
                    action: async () => {
                        return await actionExecutor.executeAction(action, {
                            submittedEntity: executionProps.submittedEntity,
                            originalEntity: executionProps.originalEntity,
                            handlers: {
                                onActionExecuted: async () => {
                                    loadingModalId = webAppContext.modals.showLoader(visualProps.message);
                                }
                            }
                        });
                    },
                    minimumDelay: visualProps.delay
                });

                if (loadingModalId) {
                    webAppContext.modals.closeModal(loadingModalId);
                }

                return result;
            },
            executeWithVisual: async (action, visualProps) => {
                const loadingModalId = webAppContext.modals.showLoader(visualProps.message);

                const actionExecutionResult = await executeWithMinimumDelay({
                    action: async () => {
                        await action();
                    },
                    minimumDelay: visualProps.delay
                });

                webAppContext.modals.closeModal(loadingModalId);

                return actionExecutionResult;
            }
        },
        clickableRef: ref,
        renderPopup: popup => webAppContext.popups.showPopUp({
            callerRef: ref,
            popup: popup
        }),
        content: () => dialog ? (
            <AgTechDialogSlot
                id={dialog.id}
                closeDialog={() => buttonClickable.dialogs.closeDialog('')}
            >
                {dialog.dialog()}
            </AgTechDialogSlot>
        ) : null
    };

    return buttonClickable;
}

export type AgTechClickEventHandler = AgTechWebClickable & {
    clickEvent: React.MouseEvent<HTMLElement>
}

export type AgTechButtonProps = {
    text?: string,
    icon?: string,
    iconOnLeft?: boolean,
    action?: (handler: AgTechClickEventHandler) => any | Promise<any>,
    children?: any,
    classes?: string,
    textClasses?: string,
    iconClasses?: string,
    disableIf?: boolean,
    styles?: React.CSSProperties,
    height?: number,
    tooltip?: string,
    role?: string,
    // Value from the taxonomy in ROLES_AND_PERMISSIONS.md (SalesProject repo), e.g. "sales.manage".
    // If both `role` and `permission` are given, the button only renders when both are satisfied.
    permission?: string,
    tabIndex?: number
}

export const AgTechButton = React.forwardRef<HTMLButtonElement, AgTechButtonProps>((props, ref) => {
    const buttonRef = useRef<HTMLButtonElement>();
    const buttonClickable = useClickable(buttonRef);
    const authContext = useAgTechAuthContext();

    // useAgTechTooltip<HTMLButtonElement>({
    //     containerRef: buttonRef,
    //     tooltip: props.tooltip
    // });

    const buttonIconOnLeft = props.iconOnLeft ?? true;

    const buttonMargin = props.icon && props.text ? 'mr-2' : '';
    const buttonIconClasses = `${buttonMargin} w-[20px] !leading-[1.4em] ${props.icon ?? ''} ${props.iconClasses ?? ''}`;

    let handleButtonClick = async (e: React.MouseEvent<HTMLElement>) => {
        if (props.action) {
            await props.action({
                ...buttonClickable,
                clickEvent: e
            });
        }
    }

    let canUserSeeButton = true;

    if (props.role) {
        canUserSeeButton = authContext.user.role.findIndex(f => f === props.role) > -1;
    }

    if (props.permission) {
        canUserSeeButton = canUserSeeButton && userHasPermission(authContext.user, props.permission);
    }

    let hasIcon = props.icon !== undefined;

    return canUserSeeButton ? (
        <button
            disabled={props.disableIf}
            className={`button ${props.classes ?? ''}`}
            onClick={handleButtonClick}
            ref={(button) => {
                if (button) {
                    buttonRef.current = button;

                    if (ref) {
                        (ref as MutableRefObject<HTMLButtonElement>).current = button;
                    }
                }
            }}
            style={{
                height: props.height ?? undefined,
                minHeight: props.height ?? undefined,
                ...props.styles
            }}
            tabIndex={props.tabIndex}
        >
            {hasIcon && buttonIconOnLeft ? <i className={buttonIconClasses} aria-hidden="true"></i> : null}
            {props.text}
            {hasIcon && !buttonIconOnLeft ? <i className={buttonIconClasses + ' ml-3'} aria-hidden="true"></i> : null}
            {props.children}
            {buttonClickable.content()}
        </button>
    ) : null;
});

export type AgTechActionButtonProps = {
    icon?: string,
    classes?: string
}

export const AgTechIconButton = (props: Omit<AgTechButtonProps, "text">) => {
    return (
        <AgTechButton
            {...props}
            text={undefined}
            iconClasses='mr-[0px]'
            classes={`button-thin ${props.classes ?? ''}`}
        />
    )
}

export const AgTechActionButton = (props: React.PropsWithChildren<AgTechActionButtonProps>) => {
    let actionButtonRef = useRef(null);
    let actionButtonClickable = useClickable(actionButtonRef);
    let actionButtonIcon = props.icon ?? 'fas fa-ellipsis-v';

    let handleActionButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        actionButtonClickable.renderPopup(() => props.children);
    }

    const customClasses = props.classes ?? '';

    return (
        <div className={`${customClasses}`} ref={actionButtonRef} onClick={handleActionButtonClick}>
            <i className={`text-base text-detail hover:text-primary cursor-pointer p-1 px-2 pointer-events-none ${actionButtonIcon}`} />
        </div>
    );
}

export type AgTechLinkProps = {
    content: string,
    body?: () => React.ReactNode,
    classes?: string,
    styles?: CSSProperties,
    onClick?: (e: AgTechClickEventHandler) => void,
    onHover?: () => AgTechPopUpCoreProps
}

export const AgTechLink = (props: AgTechLinkProps) => {
    const agtechLinkRef = useRef(null);
    const linkClickable = useClickable(agtechLinkRef);

    const handleLinkClick = (e: React.MouseEvent<HTMLElement>) => {
        if (props.onClick) {
            e.stopPropagation();
            
            props.onClick({
                ...linkClickable,
                clickEvent: e
            });
        }
    }

    return props.onHover ? (
        <div className={'flex-row items-center flexed flex'} >
            <AgTechPopUpOnHoverText
                classes={`${props.onClick ? 'agtech-link' : ''} ${props.classes ?? ''}`}
                refreshWhenChanged={props.content}
                popupRenderProps={props.onHover}
                text={props.content}
                onClicked={props.onClick}
            />
            {props.body?.()}
            {linkClickable.content()}
        </div>

    ) : (
        <div
            className={`text-sm inline-block ${props.onClick ? 'agtech-link' : ''} ${props.classes ?? ''}`} 
            style={props.styles} ref={agtechLinkRef}
            onClick={handleLinkClick}
        >
            {props.content}
            {props.body?.()}
            {linkClickable.content()}
        </div>
    )
}

// export const AgTechPopUpLink = (props: AgTechLinkProps & { handleLinkClick: (e: React.MouseEvent<HTMLElement>) => void}) => {
//     return (
//         <h1
//             className={`font-size-small inline-block ${props.onClick ? 'agtech-link' : ''} ${props.classes ?? ''}`} 
//             style={props.styles}
//             ref={agtechLinkRef}
//             onClick={props.handleLinkClick}
//         >
//             {props.content}
//         </h1>
//     ) 
// }

export type AgTechFileUploadButtonProps = AgTechButtonProps & {
    onFileUploaded: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>,
    acceptTypes?: string
}

export const AgTechFileUploadButton = (props: AgTechFileUploadButtonProps) => {
    let buttonIconOnLeft = props.iconOnLeft ?? true;
    let buttonIconClasses = `mr-2 w-[20px] ${props.icon} ${props.iconClasses ?? ''}`;

    return (
        <label className={`button ${props.classes ?? ''}`} style={{...props.styles}}>
            <form encType="multipart/form-data">
                { props.acceptTypes === undefined ? <input id="image-file" type="file" onChange={props.onFileUploaded} style={{ display: 'none' }} />
                : <input id="image-file" type="file" accept={props.acceptTypes} onChange={props.onFileUploaded} style={{ display: 'none' }} />
                }
            </form>
            {props.icon && buttonIconOnLeft ? <i className={buttonIconClasses} aria-hidden="true"></i> : null}
            {props.text}
            {props.icon && !buttonIconOnLeft ? <i className={buttonIconClasses + ' icon-right'} aria-hidden="true"></i> : null}
            {props.children}
        </label>
    )
}